import { create } from 'zustand'
import { api } from '@/api/client'
import type {
  PresalesDeal,
  PresalesKR1Result,
  PresalesKR2Result,
  PresalesKR3Result,
  PresalesKR4Result,
  KR1RollingResult,
  KR2RollingResult,
  GoalState,
  PresalesTask,
  PresalesTaskStatus,
  PresalesTaskAuditEntry,
  TaskAnalyticsResult,
  HoursAnalyticsResult,
  EventLinkSummary,
} from '@/api/types'

interface PresalesDashboardStore {
  // Deals
  deals: PresalesDeal[]
  isLoadingDeals: boolean
  dealsError: string | null

  // Filters
  ownerFilter: string | null // null = all, or specific owner name
  statusFilter: string | null
  timePeriod: string // 'current_quarter', 'last_quarter', etc.

  // KR data
  kr1: PresalesKR1Result | null
  kr2: PresalesKR2Result | null
  kr3: PresalesKR3Result | null
  kr4: PresalesKR4Result | null
  kr1Rolling: KR1RollingResult | null
  kr2Rolling: KR2RollingResult | null
  isLoadingKRs: boolean
  krsError: string | null

  // Sync status
  isSyncing: boolean
  syncError: string | null
  lastSyncedAt: string | null

  // Strategic goal tracking
  goalStates: Record<string, GoalState>
  isLoadingGoalStates: boolean

  // Tasks (per-deal, loaded on deal detail page)
  tasks: PresalesTask[]
  isLoadingTasks: boolean

  // Task audit log
  taskAuditLog: PresalesTaskAuditEntry[]
  isLoadingAuditLog: boolean

  // Person tasks (cross-deal, loaded on person tab)
  personTasks: PresalesTask[]
  isLoadingPersonTasks: boolean

  // Task analytics (performance dashboard)
  taskAnalytics: TaskAnalyticsResult | null
  isLoadingTaskAnalytics: boolean

  // Hours analytics
  hoursAnalytics: HoursAnalyticsResult | null
  isLoadingHoursAnalytics: boolean

  // Event link summary
  eventLinkSummary: EventLinkSummary | null

  // Actions
  setOwnerFilter: (owner: string | null) => void
  setStatusFilter: (status: string | null) => void
  setTimePeriod: (period: string) => void
  loadDeals: () => Promise<void>
  loadKR1: () => Promise<void>
  loadKR2: () => Promise<void>
  loadKR3: () => Promise<void>
  loadKR4: () => Promise<void>
  loadKR1Rolling: () => Promise<void>
  loadKR2Rolling: () => Promise<void>
  loadAllKRs: () => Promise<void>
  syncFromHubSpot: () => Promise<void>
  loadGoalStates: () => Promise<void>
  saveSentiment: (goalId: string, score: number) => Promise<void>
  saveGoalMetadata: (goalId: string, updates: { status?: string; due_date?: string | null }) => Promise<void>
  saveDeliverablePercentage: (deliverableId: string, percentage: number) => Promise<void>
  addGoalNote: (goalId: string, note: string) => Promise<void>
  loadTasks: (dealId: number) => Promise<void>
  createTask: (dealId: number | null, data: { template: string; description: string; context?: string; assigned_to?: string; due_date?: string; jira_ticket?: string; key_person?: string; created_by?: string; status_update?: string; prospect_company?: string }) => Promise<PresalesTask | null>
  toggleTaskStatus: (taskId: number, status: PresalesTaskStatus, completedBy?: string) => Promise<void>
  deleteTask: (taskId: number) => Promise<void>
  updateTask: (taskId: number, data: { description?: string; assigned_to?: string | null; due_date?: string | null; context?: string | null; jira_ticket?: string | null; key_person?: string | null; status_update?: string | null; deal_id?: number | null; prospect_company?: string | null }) => Promise<void>
  loadTaskAuditLog: (dealId: number) => Promise<void>
  loadPersonTasks: (assignedTo: string) => Promise<void>
  togglePersonTaskStatus: (taskId: number, status: PresalesTaskStatus, assignedTo: string, completedBy?: string) => Promise<void>
  deletePersonTask: (taskId: number, assignedTo: string) => Promise<void>
  updatePersonTask: (taskId: number, data: { description?: string; assigned_to?: string | null; due_date?: string | null; context?: string | null; jira_ticket?: string | null; key_person?: string | null; status_update?: string | null; deal_id?: number | null; prospect_company?: string | null }) => Promise<void>
  loadTaskAnalytics: () => Promise<void>
  loadHoursAnalytics: () => Promise<void>
  fetchEventLinkSummary: () => Promise<void>
  reset: () => void
}

export const usePresalesDashboardStore = create<PresalesDashboardStore>((set, get) => ({
  // Initial state
  deals: [],
  isLoadingDeals: false,
  dealsError: null,
  ownerFilter: null,
  statusFilter: null,
  timePeriod: 'current_quarter', // Default to current quarter
  kr1: null,
  kr2: null,
  kr3: null,
  kr4: null,
  kr1Rolling: null,
  kr2Rolling: null,
  isLoadingKRs: false,
  krsError: null,
  isSyncing: false,
  syncError: null,
  lastSyncedAt: null,
  goalStates: {},
  isLoadingGoalStates: false,
  tasks: [],
  isLoadingTasks: false,
  taskAuditLog: [],
  isLoadingAuditLog: false,
  personTasks: [],
  isLoadingPersonTasks: false,
  taskAnalytics: null,
  isLoadingTaskAnalytics: false,
  hoursAnalytics: null,
  isLoadingHoursAnalytics: false,
  eventLinkSummary: null,

  // Filter setters
  setOwnerFilter: (owner) => {
    set({ ownerFilter: owner })
    get().loadDeals()
  },

  setStatusFilter: (status) => {
    set({ statusFilter: status })
    get().loadDeals()
  },

  setTimePeriod: (period) => {
    set({ timePeriod: period })
    // Reload all data with new time period
    get().loadDeals()
    get().loadAllKRs()
  },

  // Load deals
  loadDeals: async () => {
    set({ isLoadingDeals: true, dealsError: null })
    const { ownerFilter, statusFilter } = get()

    try {
      const response = await api.presalesDashboard.getDeals({
        owner: ownerFilter || undefined,
        status: statusFilter || undefined,
        pageSize: 1000,
      })

      if (response.success && response.data) {
        // Derive lastSyncedAt from the most recent last_synced_at across deals
        const maxSyncedAt = response.data.items.reduce((max: string | null, d: any) => {
          if (!d.last_synced_at) return max
          return !max || d.last_synced_at > max ? d.last_synced_at : max
        }, get().lastSyncedAt)
        set({ deals: response.data.items, isLoadingDeals: false, lastSyncedAt: maxSyncedAt })
      } else {
        set({
          dealsError: response.error || 'Failed to load deals',
          isLoadingDeals: false,
          deals: [],
        })
      }
    } catch (error) {
      console.error('Error loading deals:', error)
      set({
        dealsError: error instanceof Error ? error.message : 'Network error',
        isLoadingDeals: false,
        deals: [],
      })
    }
  },

  // Load KR1
  loadKR1: async () => {
    set({ isLoadingKRs: true, krsError: null })
    const { timePeriod } = get()

    try {
      const response = await api.presalesDashboard.getKR1({ period: timePeriod })

      if (response.success && response.data) {
        set({ kr1: response.data, isLoadingKRs: false })
      } else {
        set({
          krsError: response.error || 'Failed to load KR1',
          isLoadingKRs: false,
        })
      }
    } catch (error) {
      console.error('Error loading KR1:', error)
      set({
        krsError: error instanceof Error ? error.message : 'Network error',
        isLoadingKRs: false,
      })
    }
  },

  // Load KR2
  loadKR2: async () => {
    set({ isLoadingKRs: true, krsError: null })
    const { timePeriod } = get()

    try {
      const response = await api.presalesDashboard.getKR2({ period: timePeriod })

      if (response.success && response.data) {
        set({ kr2: response.data, isLoadingKRs: false })
      } else {
        set({
          krsError: response.error || 'Failed to load KR2',
          isLoadingKRs: false,
        })
      }
    } catch (error) {
      console.error('Error loading KR2:', error)
      set({
        krsError: error instanceof Error ? error.message : 'Network error',
        isLoadingKRs: false,
      })
    }
  },

  // Load KR3
  loadKR3: async () => {
    set({ isLoadingKRs: true, krsError: null })

    try {
      const response = await api.presalesDashboard.getKR3({ quarter: get().timePeriod })

      if (response.success && response.data) {
        set({ kr3: response.data, isLoadingKRs: false })
      } else {
        set({
          krsError: response.error || 'Failed to load KR3',
          isLoadingKRs: false,
        })
      }
    } catch (error) {
      console.error('Error loading KR3:', error)
      set({
        krsError: error instanceof Error ? error.message : 'Network error',
        isLoadingKRs: false,
      })
    }
  },

  // Load KR4
  loadKR4: async () => {
    set({ isLoadingKRs: true, krsError: null })

    try {
      const response = await api.presalesDashboard.getKR4({ quarter: get().timePeriod })

      if (response.success && response.data) {
        set({ kr4: response.data, isLoadingKRs: false })
      } else {
        set({
          krsError: response.error || 'Failed to load KR4',
          isLoadingKRs: false,
        })
      }
    } catch (error) {
      console.error('Error loading KR4:', error)
      set({
        krsError: error instanceof Error ? error.message : 'Network error',
        isLoadingKRs: false,
      })
    }
  },

  // Load KR1 Rolling (TTM, Rolling 6M, cohorts)
  loadKR1Rolling: async () => {
    try {
      const response = await api.presalesDashboard.getKR1Rolling({ period: get().timePeriod })
      if (response.success && response.data) {
        set({ kr1Rolling: response.data })
      }
    } catch (error) {
      console.error('Error loading KR1 rolling:', error)
    }
  },

  // Load KR2 Rolling (TTM, Rolling 6M, cohorts)
  loadKR2Rolling: async () => {
    try {
      const response = await api.presalesDashboard.getKR2Rolling({ period: get().timePeriod })
      if (response.success && response.data) {
        set({ kr2Rolling: response.data })
      }
    } catch (error) {
      console.error('Error loading KR2 rolling:', error)
    }
  },

  // Load all KRs in parallel
  loadAllKRs: async () => {
    set({ isLoadingKRs: true, krsError: null })

    // Fire off hours analytics load in parallel (independent)
    get().loadHoursAnalytics()

    try {
      const timePeriod = get().timePeriod
      const [kr1Response, kr2Response, kr3Response, kr4Response, kr1RollingResponse, kr2RollingResponse] = await Promise.all([
        api.presalesDashboard.getKR1({ period: timePeriod }),
        api.presalesDashboard.getKR2({ period: timePeriod }),
        api.presalesDashboard.getKR3({ quarter: timePeriod }),
        api.presalesDashboard.getKR4({ quarter: timePeriod }),
        api.presalesDashboard.getKR1Rolling({ period: timePeriod }),
        api.presalesDashboard.getKR2Rolling({ period: timePeriod }),
      ])

      const updates: Partial<PresalesDashboardStore> = {
        isLoadingKRs: false,
      }

      if (kr1Response.success && kr1Response.data) {
        updates.kr1 = kr1Response.data
      }

      if (kr2Response.success && kr2Response.data) {
        updates.kr2 = kr2Response.data
      }

      if (kr3Response.success && kr3Response.data) {
        updates.kr3 = kr3Response.data
      }

      if (kr4Response.success && kr4Response.data) {
        updates.kr4 = kr4Response.data
      }

      if (kr1RollingResponse.success && kr1RollingResponse.data) {
        updates.kr1Rolling = kr1RollingResponse.data
      }

      if (kr2RollingResponse.success && kr2RollingResponse.data) {
        updates.kr2Rolling = kr2RollingResponse.data
      }

      set(updates)
    } catch (error) {
      console.error('Error loading KRs:', error)
      set({
        krsError: error instanceof Error ? error.message : 'Network error',
        isLoadingKRs: false,
      })
    }
  },

  // Sync from HubSpot
  syncFromHubSpot: async () => {
    set({ isSyncing: true, syncError: null })

    try {
      const response = await api.presalesDashboard.syncFromHubSpot()

      if (response.success) {
        set({
          isSyncing: false,
          lastSyncedAt: new Date().toISOString(),
        })

        // Reload all data after sync
        await Promise.all([get().loadDeals(), get().loadAllKRs()])
      } else {
        set({
          syncError: response.error || 'Failed to sync from HubSpot',
          isSyncing: false,
        })
      }
    } catch (error) {
      console.error('Error syncing from HubSpot:', error)
      set({
        syncError: error instanceof Error ? error.message : 'Network error',
        isSyncing: false,
      })
    }
  },

  // Load all goal states (metadata + latest sentiment)
  loadGoalStates: async () => {
    set({ isLoadingGoalStates: true })
    try {
      const response = await api.presalesDashboard.getGoalStates()
      if (response.success && response.data) {
        set({ goalStates: response.data, isLoadingGoalStates: false })
      } else {
        set({ isLoadingGoalStates: false })
      }
    } catch (error) {
      console.error('Error loading goal states:', error)
      set({ isLoadingGoalStates: false })
    }
  },

  // Save sentiment score (optimistic update, then POST)
  saveSentiment: async (goalId: string, score: number) => {
    const prev = get().goalStates
    const existing = prev[goalId] || { status: 'not_started' as const, due_date: null, latest_sentiment: null }

    // Optimistic update
    set({
      goalStates: {
        ...prev,
        [goalId]: {
          ...existing,
          latest_sentiment: { score, note: null, created_at: new Date().toISOString() },
        },
      },
    })

    try {
      const response = await api.presalesDashboard.addSentiment(goalId, { score })
      if (response.success && response.data?.latest_sentiment) {
        // Update with server timestamp
        set({
          goalStates: {
            ...get().goalStates,
            [goalId]: {
              ...get().goalStates[goalId],
              latest_sentiment: response.data.latest_sentiment,
            },
          },
        })
      }
    } catch (error) {
      console.error('Error saving sentiment:', error)
      // Revert on failure
      set({ goalStates: prev })
    }
  },

  // Save goal metadata (status and/or due date)
  saveGoalMetadata: async (goalId: string, updates: { status?: string; due_date?: string | null }) => {
    const prev = get().goalStates
    const existing = prev[goalId] || { status: 'not_started' as const, due_date: null, latest_sentiment: null }

    // Optimistic update
    set({
      goalStates: {
        ...prev,
        [goalId]: {
          ...existing,
          status: (updates.status as GoalState['status']) || existing.status,
          due_date: updates.due_date !== undefined ? updates.due_date : existing.due_date,
        },
      },
    })

    try {
      await api.presalesDashboard.updateGoalMetadata(goalId, updates)
    } catch (error) {
      console.error('Error saving goal metadata:', error)
      set({ goalStates: prev })
    }
  },

  // Save deliverable percentage (optimistic update)
  saveDeliverablePercentage: async (deliverableId: string, percentage: number) => {
    const prevKr4 = get().kr4
    if (!prevKr4) return

    // Optimistic update (include updated_at so staleness warning clears immediately)
    const updatedDeliverables = prevKr4.deliverables.map((d: any) =>
      d.id === deliverableId ? { ...d, percentage, updated_at: new Date().toISOString().replace('T', ' ').slice(0, 19) } : d
    )
    const avgPercent = updatedDeliverables.length > 0
      ? Math.round(updatedDeliverables.reduce((s: number, d: any) => s + d.percentage, 0) / updatedDeliverables.length)
      : 0
    set({
      kr4: {
        ...prevKr4,
        deliverables: updatedDeliverables,
        completion_percent: avgPercent,
        completed_count: updatedDeliverables.filter((d: any) => d.percentage === 100).length,
      },
    })

    try {
      await api.presalesDashboard.updateDeliverablePercentage(deliverableId, percentage)
    } catch (error) {
      console.error('Error saving deliverable percentage:', error)
      set({ kr4: prevKr4 }) // revert
    }
  },

  // Add a note to a goal (saves a new sentiment entry with current score)
  addGoalNote: async (goalId: string, note: string) => {
    const existing = get().goalStates[goalId]
    const currentScore = existing?.latest_sentiment?.score || 5

    try {
      const response = await api.presalesDashboard.addSentiment(goalId, { score: currentScore, note })
      if (response.success && response.data?.latest_sentiment) {
        set({
          goalStates: {
            ...get().goalStates,
            [goalId]: {
              ...get().goalStates[goalId],
              latest_sentiment: response.data.latest_sentiment,
            },
          },
        })
      }
    } catch (error) {
      console.error('Error adding goal note:', error)
    }
  },

  // ===== Presales Tasks =====

  loadTasks: async (dealId: number) => {
    set({ isLoadingTasks: true })
    try {
      const response = await api.presalesDashboard.getTasks(dealId)
      if (response.success && response.data) {
        set({ tasks: response.data, isLoadingTasks: false })
      } else {
        set({ isLoadingTasks: false })
      }
    } catch (error) {
      console.error('Error loading tasks:', error)
      set({ isLoadingTasks: false })
    }
  },

  createTask: async (dealId, data) => {
    try {
      const response = await api.presalesDashboard.createTask(dealId, data)
      if (response.success && response.data) {
        set({ tasks: [response.data, ...get().tasks] })
        // Update task counts on the deal in the deals array (only for deal-linked tasks)
        if (dealId) {
          set({
            deals: get().deals.map((d) =>
              d.id === dealId
                ? { ...d, task_count_open: (d.task_count_open || 0) + 1, task_count_total: (d.task_count_total || 0) + 1 }
                : d
            ),
          })
        }
        return response.data
      }
      return null
    } catch (error) {
      console.error('Error creating task:', error)
      return null
    }
  },

  toggleTaskStatus: async (taskId, status, completedBy) => {
    const prevTasks = get().tasks
    const task = prevTasks.find((t) => t.id === taskId)
    if (!task) return

    // Optimistic update — 'open' and 'waiting' count as open; 'completed' and 'handover' do not
    const wasOpen = task.status === 'open' || task.status === 'waiting'
    const willBeOpen = status === 'open' || status === 'waiting'
    const openDelta = wasOpen === willBeOpen ? 0 : willBeOpen ? 1 : -1

    set({
      tasks: prevTasks.map((t) =>
        t.id === taskId
          ? { ...t, status, completed_at: status === 'completed' ? new Date().toISOString() : null, completed_by: status === 'completed' ? (completedBy || null) : null }
          : t
      ),
      deals: get().deals.map((d) =>
        d.id === task.deal_id
          ? {
              ...d,
              task_count_open: Math.max(0, (d.task_count_open || 0) + openDelta),
            }
          : d
      ),
    })

    try {
      const response = await api.presalesDashboard.toggleTaskStatus(taskId, { status, completed_by: completedBy })
      if (response.success && response.data) {
        set({ tasks: get().tasks.map((t) => (t.id === taskId ? response.data! : t)) })
      }
    } catch (error) {
      console.error('Error toggling task status:', error)
      set({ tasks: prevTasks }) // Revert on failure
    }
  },

  deleteTask: async (taskId) => {
    const prevTasks = get().tasks
    const task = prevTasks.find((t) => t.id === taskId)
    if (!task) return

    // Optimistic delete
    set({
      tasks: prevTasks.filter((t) => t.id !== taskId),
      deals: get().deals.map((d) =>
        d.id === task.deal_id
          ? {
              ...d,
              task_count_open: task.status === 'open' ? Math.max(0, (d.task_count_open || 0) - 1) : d.task_count_open,
              task_count_total: Math.max(0, (d.task_count_total || 0) - 1),
            }
          : d
      ),
    })

    try {
      await api.presalesDashboard.deleteTask(taskId)
    } catch (error) {
      console.error('Error deleting task:', error)
      set({ tasks: prevTasks })
    }
  },

  updateTask: async (taskId, data) => {
    const prevTasks = get().tasks
    const task = prevTasks.find((t) => t.id === taskId)
    if (!task) return

    // Optimistic update
    set({
      tasks: prevTasks.map((t) =>
        t.id === taskId
          ? {
              ...t,
              ...(data.description !== undefined && { description: data.description }),
              ...(data.assigned_to !== undefined && { assigned_to: data.assigned_to }),
              ...(data.due_date !== undefined && { due_date: data.due_date }),
              ...(data.context !== undefined && { context: data.context }),
              ...(data.jira_ticket !== undefined && { jira_ticket: data.jira_ticket }),
              ...(data.key_person !== undefined && { key_person: data.key_person }),
              ...(data.status_update !== undefined && { status_update: data.status_update }),
              ...(data.deal_id !== undefined && { deal_id: data.deal_id }),
              ...(data.prospect_company !== undefined && { prospect_company: data.prospect_company }),
            }
          : t
      ),
    })

    try {
      const response = await api.presalesDashboard.updateTask(taskId, data)
      if (response.success && response.data) {
        set({ tasks: get().tasks.map((t) => (t.id === taskId ? response.data! : t)) })
      }
    } catch (error) {
      console.error('Error updating task:', error)
      set({ tasks: prevTasks }) // Revert on failure
    }
  },

  loadTaskAuditLog: async (dealId: number) => {
    set({ isLoadingAuditLog: true })
    try {
      const response = await api.presalesDashboard.getTaskAuditLog(dealId)
      if (response.success && response.data) {
        set({ taskAuditLog: response.data, isLoadingAuditLog: false })
      } else {
        set({ isLoadingAuditLog: false })
      }
    } catch (error) {
      console.error('Error loading task audit log:', error)
      set({ isLoadingAuditLog: false })
    }
  },

  loadPersonTasks: async (assignedTo: string) => {
    set({ isLoadingPersonTasks: true })
    try {
      const response = await api.presalesDashboard.getTasksByAssignee(assignedTo)
      if (response.success && response.data) {
        set({ personTasks: response.data, isLoadingPersonTasks: false })
      } else {
        set({ isLoadingPersonTasks: false })
      }
    } catch (error) {
      console.error('Error loading person tasks:', error)
      set({ isLoadingPersonTasks: false })
    }
  },

  togglePersonTaskStatus: async (taskId, status, _assignedTo, completedBy) => {
    const prevTasks = get().personTasks
    const task = prevTasks.find((t) => t.id === taskId)
    if (!task) return

    // Optimistic update — 'open' and 'waiting' count as open; 'completed' and 'handover' do not
    const wasOpen = task.status === 'open' || task.status === 'waiting'
    const willBeOpen = status === 'open' || status === 'waiting'
    const openDelta = wasOpen === willBeOpen ? 0 : willBeOpen ? 1 : -1

    set({
      personTasks: prevTasks.map((t) =>
        t.id === taskId
          ? { ...t, status, completed_at: status === 'completed' ? new Date().toISOString() : null, completed_by: status === 'completed' ? (completedBy || null) : null }
          : t
      ),
      deals: get().deals.map((d) =>
        d.id === task.deal_id
          ? {
              ...d,
              task_count_open: Math.max(0, (d.task_count_open || 0) + openDelta),
            }
          : d
      ),
    })

    try {
      const response = await api.presalesDashboard.toggleTaskStatus(taskId, { status, completed_by: completedBy })
      if (response.success && response.data) {
        // Preserve deal_name and company_name from the person task
        set({
          personTasks: get().personTasks.map((t) =>
            t.id === taskId ? { ...response.data!, deal_name: t.deal_name, company_name: t.company_name } : t
          ),
        })
      }
    } catch (error) {
      console.error('Error toggling person task status:', error)
      set({ personTasks: prevTasks })
    }
  },

  deletePersonTask: async (taskId, _assignedTo) => {
    const prevTasks = get().personTasks
    const task = prevTasks.find((t) => t.id === taskId)
    if (!task) return

    // Optimistic delete
    set({
      personTasks: prevTasks.filter((t) => t.id !== taskId),
      deals: get().deals.map((d) =>
        d.id === task.deal_id
          ? {
              ...d,
              task_count_open: task.status === 'open' ? Math.max(0, (d.task_count_open || 0) - 1) : d.task_count_open,
              task_count_total: Math.max(0, (d.task_count_total || 0) - 1),
            }
          : d
      ),
    })

    try {
      await api.presalesDashboard.deleteTask(taskId)
    } catch (error) {
      console.error('Error deleting person task:', error)
      set({ personTasks: prevTasks })
    }
  },

  updatePersonTask: async (taskId, data) => {
    const prevTasks = get().personTasks
    const task = prevTasks.find((t) => t.id === taskId)
    if (!task) return

    // Optimistic update
    set({
      personTasks: prevTasks.map((t) =>
        t.id === taskId
          ? {
              ...t,
              ...(data.description !== undefined && { description: data.description }),
              ...(data.assigned_to !== undefined && { assigned_to: data.assigned_to }),
              ...(data.due_date !== undefined && { due_date: data.due_date }),
              ...(data.context !== undefined && { context: data.context }),
              ...(data.jira_ticket !== undefined && { jira_ticket: data.jira_ticket }),
              ...(data.key_person !== undefined && { key_person: data.key_person }),
              ...(data.status_update !== undefined && { status_update: data.status_update }),
              ...(data.deal_id !== undefined && { deal_id: data.deal_id }),
              ...(data.prospect_company !== undefined && { prospect_company: data.prospect_company }),
            }
          : t
      ),
    })

    try {
      const response = await api.presalesDashboard.updateTask(taskId, data)
      if (response.success && response.data) {
        // Preserve deal_name and company_name from the person task
        set({
          personTasks: get().personTasks.map((t) =>
            t.id === taskId ? { ...response.data!, deal_name: t.deal_name, company_name: t.company_name } : t
          ),
        })
      }
    } catch (error) {
      console.error('Error updating person task:', error)
      set({ personTasks: prevTasks })
    }
  },

  // Load task analytics for performance dashboard
  loadTaskAnalytics: async () => {
    set({ isLoadingTaskAnalytics: true })
    try {
      const response = await api.presalesDashboard.getTaskAnalytics()
      if (response.success && response.data) {
        set({ taskAnalytics: response.data, isLoadingTaskAnalytics: false })
      } else {
        set({ isLoadingTaskAnalytics: false })
      }
    } catch (error) {
      console.error('Error loading task analytics:', error)
      set({ isLoadingTaskAnalytics: false })
    }
  },

  // Load hours analytics
  loadHoursAnalytics: async () => {
    set({ isLoadingHoursAnalytics: true })
    try {
      const response = await api.presalesDashboard.getHoursAnalytics({ period: get().timePeriod })
      if (response.success && response.data) {
        set({ hoursAnalytics: response.data, isLoadingHoursAnalytics: false })
      } else {
        set({ isLoadingHoursAnalytics: false })
      }
    } catch (error) {
      console.error('Error loading hours analytics:', error)
      set({ isLoadingHoursAnalytics: false })
    }
  },

  fetchEventLinkSummary: async () => {
    const { presalesDashboardApi } = await import('@/api/client')
    const res = await presalesDashboardApi.getEventLinkSummary()
    if (res.success && res.data) {
      set({ eventLinkSummary: res.data })
    }
  },

  // Reset store
  reset: () => {
    set({
      deals: [],
      isLoadingDeals: false,
      dealsError: null,
      ownerFilter: null,
      statusFilter: null,
      kr1: null,
      kr2: null,
      kr3: null,
      kr4: null,
      isLoadingKRs: false,
      krsError: null,
      isSyncing: false,
      syncError: null,
      lastSyncedAt: null,
      goalStates: {},
      isLoadingGoalStates: false,
      tasks: [],
      isLoadingTasks: false,
      taskAuditLog: [],
      isLoadingAuditLog: false,
      personTasks: [],
      isLoadingPersonTasks: false,
      taskAnalytics: null,
      isLoadingTaskAnalytics: false,
      hoursAnalytics: null,
      isLoadingHoursAnalytics: false,
    })
  },
}))
